import type { GetServerSidePropsContext } from 'next'
import { getBucket } from '@mc/next-growthbook'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { getControlToVariantRedirectSlug } from 'experiments/pen-2866_v1_alternative_categories/redirectSlugs'
import {
  getModalFeaturedClasses,
  QUIZ_ENTRY_IN_STICKY_FOOTER_EXP_FLAG,
} from 'experiments/pen-5646_v1_quiz_entry_sticky_footer/server'
import { trackEmailCaptureInPromoModalBackendEv } from 'experiments/pen-5891_v1_email_capture_in_promo_modal'
import {
  getAllCoursesShallow,
  getCoursesWithCategorySlug,
  getDefaultProduct,
  getFreeTrialByToken,
  getOutcomesForCourses,
  getV2Categories,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import { useSprig } from 'libs/sprig'
import { ALL_CLASSES, JUST_ADDED, TRENDING, redirect } from 'libs/utils'
import { getSubcatsCoursesOutcomes } from 'pages/categories/CategoryPage/dataFetching/getSubcatsCoursesOutcomes'
import { getTrendingInstructors } from 'pages/categories/CategoryPage/dataFetching/getTrendingInstructors'
import { AllCategoryPage } from './AllCategoryPage'
import { CategoryPage, getSubcategoriesDeprecated } from './CategoryPage'
import { type CategoryPageProps } from './pageProps'
import { redirectSlugMap } from './redirectSlugs'

export default function Categories(props: CategoryPageProps) {
  const isAllCatsPage = props.slug === ALL_CLASSES

  useSprig()

  if (isAllCatsPage) {
    return <AllCategoryPage {...props} />
  }

  return (
    <TrackingParamsProvider
      value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-categories' }}
    >
      <CategoryPage {...props} />
    </TrackingParamsProvider>
  )
}

Categories.pageSettings = {
  analyticsLabel: 'Category',
  showPromo: true,
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    query: { slug: ctxSlug = [] },
    req: {
      cookies: { referral_token: referralToken },
    },
  } = ctx

  // instead of /categories/all-classes being the same as /categories, redirect
  if (ctxSlug[0] === ALL_CLASSES) {
    return redirect(ctx, '/categories')
  }

  const slug = ctxSlug[0] || ALL_CLASSES

  // TODO: remove these redirects
  if ([JUST_ADDED, 'recently-viewed', TRENDING].includes(slug)) {
    return redirect(ctx, '/categories')
  }

  const redirectToVariantSlug = getControlToVariantRedirectSlug(slug)

  if (redirectToVariantSlug) {
    return redirect(ctx, `/categories/${redirectToVariantSlug}`)
  }

  const categories = await getV2Categories(ctx)
  const category = categories.find((c) => c.slug === slug) || null
  if (!category && slug !== ALL_CLASSES) {
    const redirectSlug = redirectSlugMap.get(slug)
    if (redirectSlug) {
      return redirect(ctx, `/categories/${redirectSlug}`)
    }
    return {
      notFound: true,
    }
  }

  const allowAltSet = true

  const quizEntryExperimentBucket = await getBucket(
    ctx,
    QUIZ_ENTRY_IN_STICKY_FOOTER_EXP_FLAG,
  )

  const shouldFetchQuizEntryClasses =
    quizEntryExperimentBucket?.startsWith('variant')

  await trackEmailCaptureInPromoModalBackendEv(ctx)

  return getPageData({
    criticalPromises: {
      courses:
        slug === ALL_CLASSES
          ? getAllCoursesShallow(ctx, { allowAltSet })
          : getCoursesWithCategorySlug(ctx, { slug, allowAltSet }),
      subcategories:
        slug !== ALL_CLASSES
          ? getSubcategoriesDeprecated(ctx, slug)
          : Promise.resolve([]),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      trial: getFreeTrialByToken(ctx, { referralToken }),
      subcatsCoursesOutcome: getSubcatsCoursesOutcomes(),
      instructors: getTrendingInstructors(),
      coursesOutcomes: getOutcomesForCourses(),
      quizEntryModalFeaturedClasses: shouldFetchQuizEntryClasses
        ? getModalFeaturedClasses(ctx)
        : Promise.resolve([]),
    },
    propsMapper: (restProps) => ({
      category,
      categories,
      slug,
      ...restProps,
    }),
  })
}
